// Phase 3 — Shared Project Memory side sheet.
//
// Six free-form sections that are auto-injected into every AI generation
// for conversations inside this project. Renders as a Linear-style slide-in
// drawer from the right (shadcn Sheet).
import { useEffect, useMemo, useRef, useState } from "react";
import { Folder, Save, Sparkles, Loader2, Check } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  EMPTY_KNOWLEDGE, KNOWLEDGE_SECTIONS, normalizeKnowledge,
  knowledgeFilledCount,
} from "@/lib/workspaceData";

export function KnowledgeBaseSheet({ open, onOpenChange, project, onSave }) {
  const [draft, setDraft] = useState(EMPTY_KNOWLEDGE);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const initialRef = useRef(EMPTY_KNOWLEDGE);

  // Hydrate when sheet opens or project changes
  useEffect(() => {
    if (!project) return;
    const k = normalizeKnowledge(project.knowledge);
    setDraft(k);
    initialRef.current = k;
    setSavedTick(false);
  }, [project?.id, project?.knowledge, open]);

  const dirty = useMemo(() => {
    return KNOWLEDGE_SECTIONS.some(
      (s) => (draft[s.key] || "") !== (initialRef.current[s.key] || "")
    );
  }, [draft]);

  const filled = useMemo(() => knowledgeFilledCount(draft), [draft]);

  const handleSave = async () => {
    if (!project || saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      initialRef.current = normalizeKnowledge(draft);
      setSavedTick(true);
      toast.success("Project memory saved");
      setTimeout(() => setSavedTick(false), 1500);
    } catch (e) {
      toast.error(e.message || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  // Autosave on close if dirty (so users don't lose work)
  const handleOpenChange = async (next) => {
    if (!next && dirty && project && !saving) {
      try { await onSave(draft); initialRef.current = normalizeKnowledge(draft); }
      catch (e) { console.warn("autosave failed", e); }
    }
    onOpenChange(next);
  };

  if (!project) return null;

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        data-testid="knowledge-base-sheet"
        className="w-full overflow-y-auto border-l border-border bg-card/95 p-0 sm:max-w-[560px]"
      >
        <div className="sticky top-0 z-10 border-b border-border bg-card/95 backdrop-blur-sm px-6 pb-4 pt-6">
          <SheetHeader className="space-y-2 text-left">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/15 text-primary">
                <Folder className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-[15px] font-semibold tracking-tight text-foreground">
                  {project.name}
                </SheetTitle>
                <SheetDescription className="text-[11.5px] text-muted-foreground">
                  Shared project memory · {filled}/{KNOWLEDGE_SECTIONS.length} filled
                </SheetDescription>
              </div>
            </div>
            <p className="flex items-start gap-2 rounded-md border border-primary/15 bg-primary/5 px-3 py-2 text-[12px] leading-relaxed text-foreground/85">
              <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>
                Every conversation inside this project automatically uses this
                as additional AI context. Be specific — the model will follow
                instructions in the last section strictly.
              </span>
            </p>
          </SheetHeader>
        </div>

        <div className="space-y-5 px-6 py-5">
          {KNOWLEDGE_SECTIONS.map((s) => (
            <div key={s.key}>
              <label
                htmlFor={`kb-${s.key}`}
                className="mb-1.5 flex items-center justify-between text-[11.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
              >
                <span>{s.label}</span>
                {(draft[s.key] || "").trim() && (
                  <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal text-primary">
                    saved
                  </span>
                )}
              </label>
              <Textarea
                id={`kb-${s.key}`}
                data-testid={`kb-${s.key}`}
                value={draft[s.key] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                placeholder={s.placeholder}
                rows={s.key === "notes" || s.key === "instructions" ? 4 : 3}
                className="resize-y rounded-lg border-border bg-background/60 text-[13px] leading-relaxed focus-visible:border-primary/40 focus-visible:ring-2 focus-visible:ring-primary/20"
              />
            </div>
          ))}
        </div>

        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-border bg-card/95 px-6 py-4 backdrop-blur-sm">
          <p className="text-[11px] text-muted-foreground">
            {dirty ? "Unsaved changes — autosaves on close" : "All changes saved"}
          </p>
          <Button
            data-testid="kb-save-btn"
            onClick={handleSave}
            disabled={saving || !dirty}
            size="sm"
            className="h-9 gap-1.5 rounded-lg"
          >
            {saving ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
            ) : savedTick ? (
              <><Check className="h-3.5 w-3.5" /> Saved</>
            ) : (
              <><Save className="h-3.5 w-3.5" /> Save memory</>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
